const noiseBuf = (c) => {
  const b = c.createBuffer(1, c.sampleRate, c.sampleRate);
  const d = b.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  return b;
};
const hz = (m) => 440 * Math.pow(2, (m - 69) / 12);

export const DRUMS = [
  { id: "kick", label: "Kick" },
  { id: "snare", label: "Snare" },
  { id: "clap", label: "Clap" },
  { id: "hat", label: "Hi-hat" },
  { id: "open", label: "Open hat" },
  { id: "tom", label: "Tom" },
];

// Synthesised drum kit + keys / synth bass / lead synth, all summed into `out`.
export class Instruments {
  constructor(ctx) {
    this.ctx = ctx;
    this.out = ctx.createGain();
    this.out.gain.value = 0.9;
    this.local = ctx.createGain(); // tap that the local mixer routes; `out` itself also feeds the live stream
    this.out.connect(this.local);
    this.noise = noiseBuf(ctx);
    this.voices = new Map();
    this.dest = null;
  }

  stream() {
    if (!this.dest) {
      this.dest = this.ctx.createMediaStreamDestination();
      this.out.connect(this.dest);
    }
    return this.dest.stream;
  }

  env(g, t, peak, decay) {
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak, t + 0.003);
    g.gain.exponentialRampToValueAtTime(0.0001, t + decay);
  }

  noiseHit(t, type, freq, peak, decay) {
    const c = this.ctx;
    const s = c.createBufferSource();
    s.buffer = this.noise;
    const f = Object.assign(c.createBiquadFilter(), { type });
    f.frequency.value = freq;
    const g = c.createGain();
    this.env(g, t, peak, decay);
    s.connect(f).connect(g).connect(this.out);
    s.start(t);
    s.stop(t + decay + 0.05);
  }

  tone(t, type, f0, f1, peak, decay) {
    const c = this.ctx;
    const o = c.createOscillator();
    o.type = type;
    o.frequency.setValueAtTime(f0, t);
    o.frequency.exponentialRampToValueAtTime(f1, t + decay * 0.6);
    const g = c.createGain();
    this.env(g, t, peak, decay);
    o.connect(g).connect(this.out);
    o.start(t);
    o.stop(t + decay + 0.05);
  }

  drum(id, t = this.ctx.currentTime) {
    if (id === "kick") this.tone(t, "sine", 150, 42, 1, 0.45);
    if (id === "snare") { this.noiseHit(t, "highpass", 1200, 0.55, 0.2); this.tone(t, "triangle", 190, 150, 0.4, 0.12); }
    if (id === "clap") [0, 0.012, 0.024].forEach((d, i) => this.noiseHit(t + d, "bandpass", 1500, i === 2 ? 0.7 : 0.4, i === 2 ? 0.22 : 0.03));
    if (id === "hat") this.noiseHit(t, "highpass", 7500, 0.3, 0.05);
    if (id === "open") this.noiseHit(t, "highpass", 7000, 0.28, 0.32);
    if (id === "tom") this.tone(t, "sine", 230, 110, 0.75, 0.35);
  }

  noteOn(inst, midi) {
    const id = `${inst}:${midi}`;
    if (this.voices.has(id)) return;
    const c = this.ctx;
    const t = c.currentTime;
    const g = c.createGain();
    const oscs = [];
    const osc = (type, freq, detune = 0, level = 1) => {
      const o = c.createOscillator();
      o.type = type;
      o.frequency.value = freq;
      o.detune.value = detune;
      const og = c.createGain();
      og.gain.value = level;
      o.connect(og);
      oscs.push(o);
      return og;
    };
    const f = hz(midi);
    let release = 0.3;
    if (inst === "keys") {
      osc("triangle", f).connect(g);
      osc("sine", f * 2, 0, 0.25).connect(g);
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.45, t + 0.005);
      g.gain.exponentialRampToValueAtTime(0.12, t + 1.2);
      g.connect(this.out);
    } else if (inst === "bass") {
      const lp = Object.assign(c.createBiquadFilter(), { type: "lowpass" });
      lp.Q.value = 6;
      lp.frequency.setValueAtTime(1800, t);
      lp.frequency.exponentialRampToValueAtTime(320, t + 0.25);
      osc("sawtooth", f).connect(lp);
      osc("square", f / 2, 0, 0.5).connect(lp);
      lp.connect(g);
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.55, t + 0.008);
      g.gain.exponentialRampToValueAtTime(0.35, t + 0.3);
      g.connect(this.out);
      release = 0.12;
    } else {
      const lp = Object.assign(c.createBiquadFilter(), { type: "lowpass" });
      lp.frequency.value = 2600;
      lp.Q.value = 2;
      osc("sawtooth", f, -8, 0.6).connect(lp);
      osc("sawtooth", f, 8, 0.6).connect(lp);
      const lfo = c.createOscillator();
      lfo.frequency.value = 5.5;
      const depth = c.createGain();
      depth.gain.value = 6;
      lfo.connect(depth);
      oscs.forEach((o) => depth.connect(o.detune));
      oscs.push(lfo);
      lp.connect(g);
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.28, t + 0.03);
      g.connect(this.out);
      release = 0.25;
    }
    oscs.forEach((o) => o.start(t));
    this.voices.set(id, { g, oscs, release });
  }

  noteOff(inst, midi) {
    const id = `${inst}:${midi}`;
    const v = this.voices.get(id);
    if (!v) return;
    this.voices.delete(id);
    const t = this.ctx.currentTime;
    v.g.gain.cancelScheduledValues(t);
    v.g.gain.setValueAtTime(Math.max(v.g.gain.value, 0.0001), t);
    v.g.gain.exponentialRampToValueAtTime(0.0001, t + v.release);
    v.oscs.forEach((o) => o.stop(t + v.release + 0.05));
  }

  allOff() { [...this.voices.keys()].forEach((id) => { const [i, m] = id.split(":"); this.noteOff(i, Number(m)); }); }
}
